import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "../css/AminiiRegister.css";
import { API_BASE_URL } from "../config/constants";

function RegisterPage() {
  const [registerMethod, setRegisterMethod] = useState("email");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [error, setError] = useState("");
  const [showOtpPopup, setShowOtpPopup] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [remainingAttempts, setRemainingAttempts] = useState(3);
  const [resendAttempts, setResendAttempts] = useState(0);
  const [resendTimer, setResendTimer] = useState(0);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  // Password validation (matches backend: 8-25 chars, 1 uppercase, 1 lowercase, 1 number, 1 special char)
  const validatePassword = (password) => {
    const regex = /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*]).{8,25}$/;
    return regex.test(password);
  };

  // Email validation
  const validateEmail = (email) => {
    const regex = /\S+@\S+\.\S+/;
    return regex.test(email);
  };

  // Start resend timer
  const startResendTimer = () => {
    setResendTimer(30);
    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Handle register method selection (for future use)
  const handleRegisterMethodSelect = (method) => {
    setRegisterMethod(method);
    setError("");
    setUsername("");
    setEmail("");
    setMobile("");
    setPassword("");
    setConfirmPassword("");
    setPasswordError("");
    setShowOtpPopup(false);
    setOtp("");
    setOtpError("");
    setRemainingAttempts(3);
    setResendAttempts(0);
    setResendTimer(0);
    setShowSuccessMessage(false);
    setShowLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setPasswordError("");

    // Validate inputs
    if (!validateEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (password !== confirmPassword) {
      setPasswordError("Passwords do not match!");
      return;
    }
    if (!validatePassword(password)) {
      setPasswordError(
        "Password must be 8-25 characters, include 1 number and 1 special character."
      );
      return;
    }

    const registerData = {
      firstName: username,
      userEmail: email,
      userPassword: password,
    };

    try {
      setShowLoading(true);
      await axios.post(
        `${API_BASE_URL}/auth/register`,
        registerData,
        { withCredentials: true }
      );
      setShowLoading(false);
      setShowOtpPopup(true);
      startResendTimer();
    } catch (err) {
      setShowLoading(false);
      const errData = err.response?.data;
      // Backend returns { success, message, errorCode, timestamp } — extract message string
      const message =
        (typeof errData === "string" ? errData : errData?.message) ||
        "Registration failed. Please try again.";
      setError(message);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setOtpError("");

    if (otp.length !== 6 || !/^\d{6}$/.test(otp)) {
      setOtpError("Please enter a valid 6-digit OTP.");
      return;
    }

    try {
      setShowLoading(true);
      const response = await axios.post(
        `${API_BASE_URL}/auth/verify-otp`,
        { email, otp },
        { withCredentials: true }
      );
      setShowLoading(false);

      // Backend returns AuthResponse with tokens on success — auto-login the user
      const responseData = response.data?.data || response.data;
      const accessToken = responseData?.accessToken || responseData?.jwt || responseData?.token;
      const refreshToken = responseData?.refreshToken;
      const userId = responseData?.userId;
      const firstName = responseData?.firstName || "";
      const userRole = responseData?.role || responseData?.userRole || "";

      if (accessToken) {
        // Store auth data just like the login page does
        localStorage.setItem("token", accessToken);
        if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
        if (userId) localStorage.setItem("userId", String(userId));
        localStorage.setItem("userFirstName", firstName);
        localStorage.setItem("userEmail", email);
        localStorage.setItem("isLoggedIn", "true");
      }

      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowOtpPopup(false);
        // Navigate to home if logged in, else to login page
        navigate(accessToken ? "/" : "/login", {
          state: accessToken ? undefined : { email },
        });
      }, 2500);
    } catch (err) {
      setShowLoading(false);
      const errData = err.response?.data;
      const message =
        (typeof errData === "string" ? errData : errData?.message) ||
        "Invalid or expired OTP. Please try again.";
      setOtpError(message);
      const match = typeof message === "string" ? message.match(/You have (\d) attempts remaining/) : null;
      if (match) {
        setRemainingAttempts(parseInt(match[1], 10));
      }
    }
  };

  const handleResendOtp = async () => {
    if (resendAttempts >= 2) {
      setOtpError("Maximum resend attempts reached.");
      return;
    }
    if (resendTimer > 0) {
      setOtpError(`Please wait ${resendTimer}s before resending OTP.`);
      return;
    }

    setOtpError("");
    setOtp("");

    try {
      setShowLoading(true);
      await axios.post(
        `${API_BASE_URL}/auth/resend-otp`,
        { email },
        { withCredentials: true }
      );
      setShowLoading(false);
      setResendAttempts((prev) => prev + 1);
      setOtpError("");
      if (resendAttempts === 1) {
        setOtpError("OTP resent to your email. Only 1 attempt left.");
      } else {
        setOtpError("OTP resent to your email.");
      }
      startResendTimer();
    } catch (err) {
      setShowLoading(false);
      const errData = err.response?.data;
      const message =
        (typeof errData === "string" ? errData : errData?.message) ||
        "Failed to resend OTP. Please try again.";
      setOtpError(message);
      const match = typeof message === "string" ? message.match(/You have (\d) attempts remaining/) : null;
      if (match) {
        setRemainingAttempts(parseInt(match[1], 10));
      }
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  return (
    <div className="register-container">
      <div className="register-box">
        <button className="auth-back-btn" onClick={() => navigate("/")} type="button">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
          Explore without login
        </button>
        <div className="brand-section" onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
          <h1 className="brand-name">A M I N I I</h1>
          <p className="brand-subtitle">C L O T H I N G</p>
        </div>

        <form onSubmit={handleRegister} className="register-form">
          <div className="register-fields-grid">
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                placeholder="Your username"
                className="input-field"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                className="input-field"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="password-input-container">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="input-field"
                />
                <button
                  type="button"
                  className="password-toggle-icon"
                  onClick={togglePasswordVisibility}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  )}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirm-password">Confirm Password</label>
              <div className="password-input-container">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirm-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="input-field"
                />
                <button
                  type="button"
                  className="password-toggle-icon"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  )}
                </button>
              </div>
              {passwordError && <p className="error-message">{passwordError}</p>}
            </div>
          </div>

          {error && <p className="error-message">{error}</p>}

          <button
            type="submit"
            className="register-button"
            disabled={showLoading}
          >
            {showLoading ? "Registering..." : "Register"}
          </button>
        </form>

        <p className="login-link">
          Already have an account?{" "}
          <a href="#" onClick={() => navigate("/login")}>
            Login
          </a>
        </p>
      </div>

      {showOtpPopup && (
        <div className="otp-popup">
          <div className="otp-popup-content">
            {showLoading ? (
              <div className="loading-animation">
                <div className="spinner"></div>
                <div className="clothing-items">
                  <div className="clothing-item item1"></div>
                  <div className="clothing-item item2"></div>
                  <div className="clothing-item item3"></div>
                </div>
                <h2>Processing...</h2>
                <p>Preparing your fashion journey</p>
              </div>
            ) : showSuccessMessage ? (
              <div className="success-animation-container">
                <div className="success-checkmark">
                  <div className="check-icon">
                    <span className="icon-line line-tip"></span>
                    <span className="icon-line line-long"></span>
                    <div className="icon-circle"></div>
                    <div className="icon-fix"></div>
                  </div>
                </div>
                <div className="fashion-runway">
                  <div className="runway-model"></div>
                  <div className="runway-lights"></div>
                </div>
                <h2 className="otp-success-title">
                  SUCCESS, WELCOME TO AMINII
                </h2>
                <p className="otp-success-message">
                  Account verified! Taking you to the store...
                </p>
              </div>
            ) : (
              <>
                <h2>Verification Required</h2>
                <p>A verification code has been sent to {email}</p>
                <form onSubmit={handleOtpSubmit} className="otp-form">
                  <div className="form-group">
                    <div className="otp-input-container">
                      <input
                        type="text"
                        id="otp"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        required
                        placeholder="Enter 6-digit code"
                        maxLength="6"
                        className="otp-input"
                      />
                    </div>
                    {otpError && (
                      <p className="otp-error-message">
                        {otpError}
                        {remainingAttempts < 3 && (
                          <span> ({remainingAttempts} attempts remaining)</span>
                        )}
                      </p>
                    )}
                  </div>
                  <button type="submit" className="otp-submit-button">
                    Verify Code
                  </button>
                  <button
                    type="button"
                    className="otp-resend-button"
                    onClick={handleResendOtp}
                    disabled={resendTimer > 0 || resendAttempts >= 2}
                  >
                    {resendTimer > 0
                      ? `Resend OTP in ${resendTimer}s`
                      : "Resend OTP"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      <div className="decorative-circle decorative-circle-1"></div>
      <div className="decorative-circle decorative-circle-2"></div>
      <div className="decorative-circle decorative-circle-3"></div>
    </div>
  );
}

export default RegisterPage;
